import React, { useEffect, useRef, useState } from "react";

/**
 * CountUp — animates a number from 0 to `value` when the element scrolls into
 * view. Re-animates when `value` changes (e.g. async data loaded after mount).
 * Optional `prefix` / `suffix` strings are rendered statically beside the
 * animating number. Respects prefers-reduced-motion.
 */
export default function CountUp({ value, duration = 1400, prefix = "", suffix = "", className = "" }) {
  const [display, setDisplay] = useState(0);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  const rafRef = useRef(null);

  // Track when element enters viewport
  useEffect(() => {
    if (!ref.current) return;
    const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setInView(true); return; }

    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.4 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  // Animate whenever value changes AND we're in view
  useEffect(() => {
    if (!inView) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setDisplay(value); return; }

    const from = 0;
    const to = Number(value) || 0;
    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setDisplay(Math.round(from + (to - from) * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [value, inView, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}
